import { useState } from 'react';
import { useMany, useTranslate } from '@refinedev/core';
import { DeleteButton, useTable } from '@refinedev/antd';
import { Button, Card, Space, Table } from 'antd';
import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import type {
  OrganizationOut,
  PublisherListOut,
} from '@/api/generated/schemas';
import { ShowIconLink } from '@/components/common/ShowIconLink';
import { orDash } from '@/components/common/show/shared';
import { ContractRatesTable } from '@/components/tenancy/ContractRatesTable';
import {
  isContractEditable,
  type ContractOut,
} from '@/pages/tenancy/contracts/constants';
import { formatDate, formatDateTime } from '@/utils/format';
import { getSortOrder } from '@/utils/sorters';
import { ContractFormModal } from './ContractFormModal';

interface OrganizationContractsTabProps {
  record: OrganizationOut;
}

export const OrganizationContractsTab = ({
  record,
}: OrganizationContractsTabProps) => {
  const t = useTranslate();
  const [modalState, setModalState] = useState<{
    open: boolean;
    contract: ContractOut | null;
  }>({ open: false, contract: null });

  const { tableProps, sorters, result } = useTable<ContractOut>({
    resource: 'tenancy.contracts',
    queryOptions: { enabled: record.id != null },
    filters: {
      permanent: [
        { field: 'organization_id', operator: 'eq', value: record.id },
      ],
    },
    sorters: {
      // publisher_id desc floats the org-default contract (NULL) to the
      // top — Postgres puts NULLs first on descending order.
      initial: [
        { field: 'publisher_id', order: 'desc' },
        { field: 'valid_from', order: 'desc' },
      ],
    },
    pagination: { pageSize: 10 },
    syncWithLocation: false,
  });

  const contracts = result?.data ?? [];

  const publisherIds = [
    ...new Set(
      contracts
        .map((c) => c.publisher_id)
        .filter((id): id is number => id != null)
    ),
  ];
  const { result: publishersResult } = useMany<PublisherListOut>({
    resource: 'user_catalog.publishers',
    ids: publisherIds,
    queryOptions: { enabled: publisherIds.length > 0 },
  });
  const publisherMap = new Map(
    (publishersResult?.data ?? []).map((p: PublisherListOut) => [p.id, p])
  );

  return (
    <>
      <Card
        title={`${t('tenancy.organizations.sections.contracts')} (${result?.total ?? 0})`}
        extra={
          <Button
            icon={<PlusOutlined />}
            onClick={() => setModalState({ open: true, contract: null })}
          >
            {t('tenancy.contracts.actions.add_contract')}
          </Button>
        }
      >
        <Table<ContractOut>
          {...tableProps}
          rowKey="id"
          size="small"
          expandable={{
            expandedRowRender: (contract) => (
              <ContractRatesTable rates={contract.rates} />
            ),
          }}
        >
          <Table.Column<ContractOut>
            dataIndex="id"
            title={t('base.fields.id')}
          />

          <Table.Column<ContractOut>
            dataIndex="publisher_id"
            title={t('tenancy.contracts.fields.publisher')}
            render={(publisherId: number | null) =>
              orDash(
                publisherId != null ? (
                  <ShowIconLink
                    resource="user_catalog.publishers"
                    id={publisherId}
                  >
                    {publisherMap.get(publisherId)?.ancestry_with_name ??
                      publisherId}
                  </ShowIconLink>
                ) : (
                  t('tenancy.contracts.publisher_default')
                )
              )
            }
          />
          <Table.Column<ContractOut>
            dataIndex="valid_from"
            title={t('tenancy.contracts.fields.valid_from')}
            sorter
            sortOrder={getSortOrder(sorters, 'valid_from')}
            render={(value: string) => formatDate(value)}
          />
          <Table.Column<ContractOut>
            dataIndex="rates"
            title={t('tenancy.contracts.fields.rates')}
            render={(rates: ContractOut['rates']) => rates?.length ?? 0}
          />
          <Table.Column<ContractOut>
            dataIndex="updated_at"
            title={t('base.fields.updated_at')}
            sorter
            sortOrder={getSortOrder(sorters, 'updated_at')}
            render={(value: string) => formatDateTime(value)}
          />
          <Table.Column<ContractOut>
            dataIndex="actions"
            title={t('actions.column')}
            render={(_, contract) =>
              // Contracts already in effect are immutable.
              isContractEditable(contract) && (
                <Space>
                  <Button
                    icon={<EditOutlined />}
                    size="small"
                    onClick={() => setModalState({ open: true, contract })}
                  />
                  <DeleteButton
                    hideText
                    size="small"
                    resource="tenancy.contracts"
                    recordItemId={contract.id}
                  />
                </Space>
              )
            }
          />
        </Table>
      </Card>

      <ContractFormModal
        open={modalState.open}
        onClose={() => setModalState({ open: false, contract: null })}
        record={record}
        contract={modalState.contract}
      />
    </>
  );
};
